import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationsAlerts.css';

const NotificationsAlerts = () => {
  const { t } = useTranslation('navigation');
  const [notifications, setNotifications] = useState([]);
  
  // Helper functions to translate severity and type
  const translateSeverity = (severity) => {
    if (!severity) return '';
    const severityMap = {
      'critical': t('notifications.filters.critical'),
      'high': t('notifications.filters.high'),
      'medium': t('notifications.filters.medium'),
      'low': t('notifications.filters.low')
    };
    return severityMap[severity.toLowerCase()] || severity;
  };
  
  const translateType = (type) => {
    if (!type) return '';
    const typeMap = {
      'system': t('notifications.filters.system'),
      'user': t('notifications.filters.user'),
      'content': t('notifications.filters.content'),
      'security': t('notifications.filters.security'),
      'engagement': t('notifications.filters.engagement'),
      'smart': t('notifications.filters.smart'),
      'communication': t('notifications.filters.communication'),
      'scheduled': t('notifications.filters.scheduled')
    };
    return typeMap[type.toLowerCase()] || type;
  };
  
  // Helper function to translate notification titles
  const translateNotificationTitle = (title) => {
    if (!title) return '';
    const titleMap = {
      'High CPU Usage Alert': t('notifications.titles.highCpuUsageAlert'),
      'New Teacher Registration': t('notifications.titles.newTeacherRegistration'),
      'Course Pending Approval': t('notifications.titles.coursePendingApproval'),
      'Suspicious Login Attempt': t('notifications.titles.suspiciousLoginAttempt'),
      'Student Activity Drop': t('notifications.titles.studentActivityDrop'),
      'High-Performing Teacher Alert': t('notifications.titles.highPerformingTeacherAlert'),
      'New Support Ticket': t('notifications.titles.newSupportTicket'),
      'Scheduled Maintenance': t('notifications.titles.scheduledMaintenance')
    };
    return titleMap[title] || title;
  };
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all-notifications');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [floatingMessage, setFloatingMessage] = useState(null);
  const [settings, setSettings] = useState({
    realTimeAlerts: true,
    userActivityAlerts: true,
    contentAlerts: true,
    communicationAlerts: true,
    securityAlerts: true,
    engagementAlerts: true,
    smartAlerts: true,
    scheduledAlerts: true,
    channels: {
      inApp: true,
      email: true,
      sms: false,
      push: true
    },
    thresholds: {
      userGrowthDrop: 10,
      errorRateThreshold: 5,
      inactivityThreshold: 7
    }
  });

  // Mock data for notifications
  useEffect(() => {
    setTimeout(() => {
      const mockNotifications = [
        {
          id: 1,
          type: 'system',
          category: 'performance',
          title: 'High CPU Usage Alert',
          message: 'Server CPU usage detected at 85%',
          severity: 'high',
          timestamp: '2024-01-15 10:30:00',
          read: false,
          actionRequired: true,
          user: 'System Monitor'
        },
        {
          id: 2,
          type: 'user',
          category: 'registration',
          title: 'New Teacher Registration',
          message: 'Dr. Sarah Johnson has registered as a teacher',
          severity: 'medium',
          timestamp: '2024-01-15 09:15:00',
          read: false,
          actionRequired: false,
          user: 'Sarah Johnson'
        },
        {
          id: 3,
          type: 'content',
          category: 'submission',
          title: 'Course Pending Approval',
          message: 'New course "Advanced English Grammar" submitted',
          severity: 'medium',
          timestamp: '2024-01-15 08:45:00',
          read: true,
          actionRequired: true,
          user: 'Teacher ID: 1234'
        },
        {
          id: 4,
          type: 'security',
          category: 'login',
          title: 'Suspicious Login Attempt',
          message: 'Multiple failed login attempts detected',
          severity: 'high',
          timestamp: '2024-01-15 08:20:00',
          read: false,
          actionRequired: true,
          user: 'admin@nativelike.com'
        },
        {
          id: 5,
          type: 'engagement',
          category: 'retention',
          title: 'Student Activity Drop',
          message: '30% drop in student engagement detected',
          severity: 'medium',
          timestamp: '2024-01-15 07:30:00',
          read: true,
          actionRequired: false,
          user: 'School District A'
        },
        {
          id: 6,
          type: 'smart',
          category: 'prediction',
          title: 'High-Performing Teacher Alert',
          message: 'Teacher Maria Rodriguez shows exceptional engagement',
          severity: 'low',
          timestamp: '2024-01-15 06:45:00',
          read: false,
          actionRequired: false,
          user: 'Maria Rodriguez'
        },
        {
          id: 7,
          type: 'communication',
          category: 'support',
          title: 'New Support Ticket',
          message: 'Priority ticket #1234: Payment processing error',
          severity: 'high',
          timestamp: '2024-01-15 06:00:00',
          read: false,
          actionRequired: true,
          user: 'user@example.com'
        },
        {
          id: 8,
          type: 'scheduled',
          category: 'maintenance',
          title: 'Scheduled Maintenance',
          message: 'System maintenance scheduled for tomorrow',
          severity: 'low',
          timestamp: '2024-01-15 05:30:00',
          read: true,
          actionRequired: false,
          user: 'System Admin'
        }
      ];
      setNotifications(mockNotifications);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || notification.severity === filterSeverity;
    const matchesType = filterType === 'all' || notification.type === filterType;
    
    return matchesSearch && matchesSeverity && matchesType;
  });

  const handleMarkAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const handleDeleteNotification = (id) => {
    if (window.confirm(t('notifications.actions.deleteConfirm'))) {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'system': return '🔧';
      case 'user': return '👤';
      case 'content': return '📝';
      case 'security': return '🔒';
      case 'engagement': return '📊';
      case 'smart': return '🧠';
      case 'communication': return '💬';
      case 'scheduled': return '📅';
      default: return '🔔';
    }
  };

  const tabs = [
    { id: 'all-notifications', label: t('notifications.tabs.allNotifications'), icon: 'notifications' },
    { id: 'system-alerts', label: t('notifications.tabs.systemAlerts'), icon: 'warning' },
    { id: 'user-activity', label: t('notifications.tabs.userActivity'), icon: 'people' },
    { id: 'content-moderation', label: t('notifications.tabs.contentModeration'), icon: 'content_paste' },
    { id: 'security-threats', label: t('notifications.tabs.securityThreats'), icon: 'security' },
    { id: 'engagement-analytics', label: t('notifications.tabs.engagementAnalytics'), icon: 'analytics' },
    { id: 'smart-insights', label: t('notifications.tabs.smartInsights'), icon: 'psychology' },
    { id: 'communication', label: t('notifications.tabs.communication'), icon: 'message' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'all-notifications':
        return (
          <div className="tab-content">
            <div className="notification-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('notifications.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <div className="filter-controls">
                <select 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">{t('notifications.filters.allTypes')}</option>
                  <option value="system">{t('notifications.filters.system')}</option>
                  <option value="user">{t('notifications.filters.user')}</option>
                  <option value="content">{t('notifications.filters.content')}</option>
                  <option value="security">{t('notifications.filters.security')}</option>
                  <option value="engagement">{t('notifications.filters.engagement')}</option>
                  <option value="smart">{t('notifications.filters.smart')}</option>
                  <option value="communication">{t('notifications.filters.communication')}</option>
                  <option value="scheduled">{t('notifications.filters.scheduled')}</option>
                </select>
                
                <select 
                  value={filterSeverity} 
                  onChange={(e) => setFilterSeverity(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">{t('notifications.filters.allSeverities')}</option>
                  <option value="critical">{t('notifications.filters.critical')}</option>
                  <option value="high">{t('notifications.filters.high')}</option>
                  <option value="medium">{t('notifications.filters.medium')}</option>
                  <option value="low">{t('notifications.filters.low')}</option>
                </select>
              </div>
              
              <div className="action-buttons-group">
                <button 
                  className="action-btn secondary"
                  onClick={handleMarkAllAsRead}
                >
                  <span className="material-icons">mark_email_read</span>
                  {t('notifications.actions.markAllRead')}
                </button>
                
                <button 
                  className="action-btn primary"
                  onClick={() => setShowSettings(true)}
                >
                  <span className="material-icons">settings</span>
                  {t('notifications.actions.settings')}
                </button>
              </div>
            </div>

            <div className="notifications-table-container">
              <table className="notifications-table">
                  <thead>
                    <tr>
                      <th>{t('notifications.tableHeaders.type')}</th>
                      <th>{t('notifications.tableHeaders.title')}</th>
                      <th>{t('notifications.tableHeaders.severity')}</th>
                      <th>{t('notifications.tableHeaders.userSource')}</th>
                      <th>{t('notifications.tableHeaders.timestamp')}</th>
                      <th>{t('notifications.tableHeaders.status')}</th>
                      <th>{t('notifications.tableHeaders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNotifications.map(notification => (
                      <tr key={notification.id} className={notification.read ? 'read' : 'unread'}>
                        <td>
                          <div className="notification-type">
                            <span className="type-icon">{getTypeIcon(notification.type)}</span>
                            <span className="type-label">{translateType(notification.type)}</span>
                          </div>
                        </td>
                        <td 
                          className="notification-title clickable" 
                          onClick={() => setFloatingMessage(notification)}
                          title={t('notifications.actions.clickToView')}
                        >
                          {translateNotificationTitle(notification.title)}
                        </td>
                        <td>
                          <span 
                            className="severity-badge"
                            style={{ backgroundColor: getSeverityColor(notification.severity) }}
                          >
                            {translateSeverity(notification.severity)}
                          </span>
                        </td>
                        <td>{notification.user}</td>
                        <td>{notification.timestamp}</td>
                        <td>
                          <div className="status-indicators">
                            <span className={`read-status ${notification.read ? 'read' : 'unread'}`}>
                              {notification.read ? t('notifications.status.read') : t('notifications.status.unread')}
                            </span>
                            {notification.actionRequired && (
                              <span className="action-required">{t('notifications.status.actionRequired')}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-btn edit"
                              onClick={() => handleMarkAsRead(notification.id)}
                              title={notification.read ? t('notifications.actions.markAsUnread') : t('notifications.actions.markAsRead')}
                            >
                              <span className="material-icons">
                                {notification.read ? 'mark_email_unread' : 'mark_email_read'}
                              </span>
                            </button>
                            <button 
                              className="action-btn delete"
                              onClick={() => handleDeleteNotification(notification.id)}
                              title={t('notifications.actions.deleteNotification')}
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
              
              {filteredNotifications.length === 0 && (
                <div className="no-notifications">
                  <span className="material-icons">notifications_off</span>
                  <p>{t('notifications.emptyState.noNotifications')}</p>
                </div>
              )}
          </div>
        );

      case 'system-alerts':
        return (
          <div className="tab-content">
            <h3>System Alerts</h3>
            <p>Monitor server performance, database health, and system errors.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">build</span>
              <p>System monitoring features coming soon...</p>
            </div>
          </div>
        );

      case 'user-activity':
        return (
          <div className="tab-content">
            <h3>User Activity Alerts</h3>
            <p>Track user registrations, milestones, and account activities.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">people</span>
              <p>User activity monitoring features coming soon...</p>
            </div>
          </div>
        );

      case 'content-moderation':
        return (
          <div className="tab-content">
            <h3>Content Moderation</h3>
            <p>Manage content submissions, flags, and quality control.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">content_paste</span>
              <p>Content moderation features coming soon...</p>
            </div>
          </div>
        );

      case 'security-threats':
        return (
          <div className="tab-content">
            <h3>Security Threats</h3>
            <p>Monitor security threats, login attempts, and access control.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">security</span>
              <p>Security monitoring features coming soon...</p>
            </div>
          </div>
        );

      case 'engagement-analytics':
        return (
          <div className="tab-content">
            <h3>Engagement Analytics</h3>
            <p>Track student engagement, teacher performance, and retention metrics.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">analytics</span>
              <p>Engagement analytics features coming soon...</p>
            </div>
          </div>
        );

      case 'smart-insights':
        return (
          <div className="tab-content">
            <h3>Smart Insights</h3>
            <p>AI-powered insights, predictions, and recommendations.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">psychology</span>
              <p>Smart insights features coming soon...</p>
            </div>
          </div>
        );

      case 'communication':
        return (
          <div className="tab-content">
            <h3>Communication</h3>
            <p>Manage support tickets, chat requests, and feedback.</p>
            <div className="feature-coming-soon">
              <span className="material-icons">message</span>
              <p>Communication features coming soon...</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="tab-content">
            <h3>Notifications & Alerts</h3>
            <p>Select a tab to view specific notification categories.</p>
          </div>
        );
    }
  };

  const renderSettingsForm = () => {
    if (!showSettings) return null;

    return (
      <div className="settings-overlay">
        <div className="settings-form">
          <h3>Notification Settings</h3>
          
          <div className="settings-section">
            <h4>Alert Types</h4>
            <div className="settings-grid">
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.realTimeAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, realTimeAlerts: e.target.checked }))}
                />
                <span>Real-Time System Alerts</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.userActivityAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, userActivityAlerts: e.target.checked }))}
                />
                <span>User Activity Alerts</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.contentAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, contentAlerts: e.target.checked }))}
                />
                <span>Content & Course Alerts</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.communicationAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, communicationAlerts: e.target.checked }))}
                />
                <span>Communication Notifications</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.securityAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, securityAlerts: e.target.checked }))}
                />
                <span>Security & Access Alerts</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.engagementAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, engagementAlerts: e.target.checked }))}
                />
                <span>Engagement & Retention Alerts</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.smartAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, smartAlerts: e.target.checked }))}
                />
                <span>Smart Alerts (AI-based)</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.scheduledAlerts}
                  onChange={(e) => setSettings(prev => ({ ...prev, scheduledAlerts: e.target.checked }))}
                />
                <span>Scheduled Notifications</span>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h4>Notification Channels</h4>
            <div className="settings-grid">
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.channels.inApp}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    channels: { ...prev.channels, inApp: e.target.checked }
                  }))}
                />
                <span>In-App Notifications</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.channels.email}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    channels: { ...prev.channels, email: e.target.checked }
                  }))}
                />
                <span>Email Notifications</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.channels.sms}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    channels: { ...prev.channels, sms: e.target.checked }
                  }))}
                />
                <span>SMS Notifications</span>
              </label>
              <label className="setting-item">
                <input
                  type="checkbox"
                  checked={settings.channels.push}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    channels: { ...prev.channels, push: e.target.checked }
                  }))}
                />
                <span>Push Notifications</span>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h4>Alert Thresholds</h4>
            <div className="threshold-inputs">
              <div className="threshold-item">
                <label>User Growth Drop (%)</label>
                <input
                  type="number"
                  value={settings.thresholds.userGrowthDrop}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    thresholds: { ...prev.thresholds, userGrowthDrop: parseInt(e.target.value) }
                  }))}
                  min="0"
                  max="100"
                />
              </div>
              <div className="threshold-item">
                <label>Error Rate Threshold (%)</label>
                <input
                  type="number"
                  value={settings.thresholds.errorRateThreshold}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    thresholds: { ...prev.thresholds, errorRateThreshold: parseInt(e.target.value) }
                  }))}
                  min="0"
                  max="100"
                />
              </div>
              <div className="threshold-item">
                <label>Inactivity Threshold (days)</label>
                <input
                  type="number"
                  value={settings.thresholds.inactivityThreshold}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    thresholds: { ...prev.thresholds, inactivityThreshold: parseInt(e.target.value) }
                  }))}
                  min="1"
                  max="30"
                />
              </div>
            </div>
          </div>

          <div className="form-buttons">
            <button 
              className="save-btn"
              onClick={() => {
                alert('Settings saved successfully!');
                setShowSettings(false);
              }}
            >
              Save Settings
            </button>
            <button 
              className="cancel-btn"
              onClick={() => setShowSettings(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading notifications...</p>
      </div>
    );
  }

  return (
    <div id="notifications-alerts" className="section">
      <div className="notifications-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
      
      {renderTabContent()}
      {renderSettingsForm()}
      
      {floatingMessage && (
        <div className="floating-message-box" onClick={() => setFloatingMessage(null)}>
          <div className="message-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-btn" onClick={() => setFloatingMessage(null)}>×</span>
            <h4>{floatingMessage.title}</h4>
            <p>{floatingMessage.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsAlerts; 